import * as Backbone from "backbone";
import * as $ from "jquery";
import * as _ from "lodash";
import { PermissionCollection } from "../../../collections/Permissions";
import { ReportsCollection } from "../../../collections/Reports";
import { UserGroupCollection } from "../../../collections/UserGroups";
import { GenericDeleteModal } from "../GenericDeleteModal";
import { BaseModifyModalView, BaseModifyModalViewOptions } from "../_BaseModifyModal";

interface ModifyReportModalOptions extends BaseModifyModalViewOptions {
    isClone: boolean;
    reportsCollection: ReportsCollection;
    reportId: number;
    orgId: number;
}

export class ModifyReportModal extends BaseModifyModalView {
    public options: ModifyReportModalOptions;

    public userGroupCollection: UserGroupCollection = null;
    public userGroupPermissionsCollections: { [key: number]: PermissionCollection } = {};
    private report: Backbone.Model;
    private clonedReport: Backbone.Model;
    private modifyTemplate = _.template(require("../../../templates/modals/reports/modifyReport.html"));
    private cloneTemplate = _.template(require("../../../templates/modals/reports/cloneReport.html"));

    constructor(options: ModifyReportModalOptions) {
        super(
            <any>_.extend(options, {
                tagName: "div",
                events: {
                    "shown.bs.modal #addReportModal": "handleModalShown",
                    "hidden.bs.modal #addReportModal": "handleModalHidden",
                    "change .permission-checkbox": "handlePermissionChange",
                    "click .add-report-btn": "handleAddReportClick",
                    "click .clone-report-btn": "handleCloneReportClick",
                    "click .delete-report-btn": "handleDeleteReportClick",
                    "input .add-report-name": "handleInputReportName",
                },
            })
        );

        // For base class methods
        this.modalIdSelector = "addReportModal";

        this.handleInputReportName = _.debounce(this.handleInputReportName.bind(this), 100);

        if (this.options.reportId) {
            this.report = this.options.reportsCollection.findWhere({ id: this.options.reportId });
        } else {
            this.report = new this.options.reportsCollection.model({
                name: "New Report",
            });
        }

        // We set the erport ID to the original report we want to clone
        // so we can clone the perimssions
        if (this.options.isClone) {
            this.options.modelId = this.options.reportId;
        }
    }

    render() {
        const template = this.options.isClone ? this.cloneTemplate : this.modifyTemplate;
        this.$el.html(
            template({
                report: this.report,
            })
        );

        this.delegateEvents();
        return this;
    }

    show() {
        super.show();
        // Load permissions & render toggle buttons
        $.when(
            this.loadPermissionsCollections(this.options.orgId, "canViewReport", "canModifyReport", "canDeleteReport")
        ).then(() => {
            this.handleAddButtonDisabledState();
        });
    }

    handleInputReportName() {
        this.handleAddButtonDisabledState();
    }

    handleAddButtonDisabledState() {
        var canAssignPerms = this.canAssignPerms();
        if (canAssignPerms === null) return;

        // Make sure the report is named
        if (!this.getName() || !canAssignPerms) {
            this.setButtonsDisabled(true);
            return;
        }
        // Make sure at least one dashboard has all permissions
        var allPerms = _.some(
            this.$(".permission-modify").map(function () {
                return $(this).is(":checked");
            })
        );
        this.setButtonsDisabled(!allPerms);
    }

    getName() {
        return (<string>this.$(".add-report-name").val()).trim();
    }

    handleAddReportClick() {
        this.setFormSubmitted();
        if (!this.report.collection) this.options.reportsCollection.add(this.report);
        var saveReport = $.ajax({
            type: "POST",
            url: this.options.reportsCollection.url() + "/" + this.options.orgId,
            data: JSON.stringify({
                name: <string>this.$(".add-report-name").val(),
                format: <string>this.$(".add-report-format").val(),
                permissions: this.getPermissionsDict(),
                timezone: this.options.currentUser.get("timezone"),
            }),
            dataType: "json",
            contentType: "application/json; charset=utf-8",
            complete: (res) => {},
        });

        saveReport
            .then((response: any) => {
                this.refetchCollectionsAndShow(Number(response.reportId));
            })
            .fail(() => {
                this.setFormError();
            });
    }

    private handleCloneReportClick(e: JQuery.UIEventBase) {
        e.preventDefault();
        this.setFormSubmitted();

        const originalReport = this.report;
        const reportName = <string>this.$(".add-report-name").val();

        $.ajax({
            type: "POST",
            url: this.options.reportsCollection.cloneUrl(originalReport.get("id")),
            data: JSON.stringify({
                organizationId: this.options.orgId,
                originalReport: originalReport,
                reportName: reportName,
                originalReportPermissions: this.getPermissionsDict(),
            }),
            dataType: "json",
            contentType: "application/json; charset=utf-8",
            success: (res) => {
                this.refetchCollectionsAndShow(Number(res.reportId));
            },
            error: (jqXHR) => {
                let error = ["Unable to process your request."];
                if (jqXHR && jqXHR.responseJSON && typeof jqXHR.responseJSON.message === "string") {
                    error = [jqXHR.responseJSON.message];
                }

                (<any>$).notify(
                    {
                        title: "Something went wrong",
                        message: error,
                        icon: "fa fa-exclamation-circle",
                    },
                    {
                        animate: {
                            enter: "animated fadeInUp",
                        },
                        // The type property defines the css style
                        // See alerts.less for available styles
                        type: "warning-alt",
                        placement: { from: "bottom", align: "center" },
                    }
                );

                this.$('.modal-footer button').removeClass('hidden');
                this.$('.modal-footer .modal-form-spinner').addClass('hidden');
            },
        });
    }

    handleDeleteReportClick() {
        var modal = new GenericDeleteModal({
            type: "Report",
            name: this.report.get("name"),
            func: () => {
                this.setFormSubmitted();
                return $.when(this.report.destroy()).done(() => {
                    this.refetchCollectionsAndShow();
                });
            },
        });
        $(".modals").append(modal.render().el);
        modal.show();
    }

    refetchCollectionsAndShow(reportId?) {
        $.when(
            this.options.reportsCollection.fetch({ reset: true }),
            this.options.permissionCollection.fetch({ reset: true })
        )
            .done(() => {
                this.hide();

                // Render EditReportsView (beginning with the Destination subview)
                window.location.hash = "#/reports/" + this.options.orgId + (reportId ? "/" + reportId + "/edit" : "");

                window['App'].Bus.trigger('navigation:update');
            })
            .fail(() => {
                this.setFormError();
            });
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/modals/reports/ModifyReportModal.ts